import {CAMPOS_EN_LOTE_DE_BIG_BAGS, type LoteDeBigBags, type ResultadoDeLoteDeBigBags} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCambioEnLoteDeBigBags} from '../api/inventario';
import {tr, trx} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_CAMBIOS_BIG_BAGS, ayudaDeBigBag} from './ayuda-inventario';
import {CAMPO_BIG_BAG, convertirValor, tipoDeEntrada} from './campos-big-bag';
import {NotaDeAyuda} from './nota-de-ayuda';

export type CampoEnLote = (typeof CAMPOS_EN_LOTE_DE_BIG_BAGS)[number];
const texto = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '—' : String(v));

// Cambiar el mismo dato en los Big Bags marcados (6/10/2026): se elige el dato
// y el valor, se revisa que cambia en cada bolsa y se aplica todo junto. Solo
// datos descriptivos; pesos, tenores, gramos y sellos van uno por uno.
// En Calidad de datos (7/10/2026) llega con el dato de la revision ya elegido.
export function CambioEnLoteDeBigBags({
  bolsas,
  onListo,
  campoInicial = 'mesMolienda',
}: {
  bolsas: string[];
  onListo: () => void;
  campoInicial?: CampoEnLote;
}) {
  const cambiar = useCambioEnLoteDeBigBags();
  const [abierto, setAbierto] = useState(false);
  const [campo, setCampo] = useState<CampoEnLote>(campoInicial);
  const [valor, setValor] = useState('');
  const [revision, setRevision] = useState<ResultadoDeLoteDeBigBags | null>(null);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const definicion = CAMPO_BIG_BAG.get(campo)!;

  const pedido = (modo: 'revisar' | 'aplicar'): LoteDeBigBags | null => {
    const convertido = convertirValor(definicion, valor);
    if ('error' in convertido) {
      setMensaje({texto: convertido.error, error: true});
      return null;
    }
    return {modo, codigos: bolsas, cambios: {[campo]: convertido.valor} as LoteDeBigBags['cambios']};
  };
  const ejecutar = async (modo: 'revisar' | 'aplicar') => {
    setMensaje(null);
    const cuerpo = pedido(modo);
    if (!cuerpo) return;
    try {
      const r = await cambiar.mutateAsync(cuerpo);
      if (modo === 'revisar') return setRevision(r);
      setMensaje({
        texto: tr(`Listo: ${numero(r.cambian)} Big Bags cambiados.`, `Done: ${numero(r.cambian)} Big Bags changed.`),
        error: false,
      });
      setRevision(null);
      setAbierto(false);
      setValor('');
      onListo();
    } catch (error) {
      setMensaje({
        texto:
          error instanceof ErrorDeApi ? error.message : tr('No se pudo hacer el cambio.', 'Could not make the change.'),
        error: true,
      });
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={!bolsas.length}
        onClick={() => {
          setAbierto(v => !v);
          setRevision(null);
          setMensaje(null);
        }}
        title={tr(
          'Pone el mismo dato (mina, bodega, mes de molienda...) en todos los Big Bags marcados',
          'Sets the same value (mine, warehouse, grinding month...) on all the ticked Big Bags',
        )}
        className="rounded-md border border-linea bg-white px-2 py-0.5 text-xs hover:bg-papel disabled:opacity-40"
      >
        ⇶ {tr('Cambiar datos', 'Change data')}
        {bolsas.length ? ` (${numero(bolsas.length)})` : ''}
      </button>
      {mensaje && !abierto && (
        <span role="status" className={`text-xs ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
          {mensaje.texto}
        </span>
      )}
      {abierto && (
        <div
          role="region"
          aria-label={tr('Cambiar datos de los Big Bags marcados', 'Change data of the ticked Big Bags')}
          className="basis-full rounded-lg border border-verde/40 bg-white p-3 text-sm"
        >
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={e => {
              e.preventDefault();
              void ejecutar('revisar');
            }}
          >
            <label className="block text-xs text-apagado">
              {tr('Dato', 'Field')}
              <NotaDeAyuda texto={AYUDA_CAMBIOS_BIG_BAGS.dato} dentroDeEtiqueta />
              <select
                value={campo}
                onChange={e => {
                  setCampo(e.target.value as CampoEnLote);
                  setValor('');
                  setRevision(null);
                }}
                className="mt-0.5 block rounded-md border border-linea px-2 py-1 text-sm text-tinta"
              >
                {CAMPOS_EN_LOTE_DE_BIG_BAGS.map(c => (
                  <option key={c} value={c}>
                    {trx(CAMPO_BIG_BAG.get(c)!.etiqueta)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-apagado">
              {tr('Valor nuevo', 'New value')}
              <NotaDeAyuda
                titulo={definicion.etiqueta}
                texto={ayudaDeBigBag(campo) ?? AYUDA_CAMBIOS_BIG_BAGS.valorNuevo}
                dentroDeEtiqueta
              />
              <input
                type={tipoDeEntrada(definicion.tipo)}
                list={definicion.lista ? `opciones-bb-${definicion.lista}` : undefined}
                value={valor}
                onChange={e => {
                  setValor(e.target.value);
                  setRevision(null);
                }}
                placeholder={tr('Vacío = borrar el dato', 'Empty = clear the value')}
                className="mt-0.5 block w-56 rounded-md border border-linea px-2 py-1 text-sm text-tinta"
              />
            </label>
            <button
              type="submit"
              disabled={cambiar.isPending}
              className="rounded-md border border-linea bg-white px-3 py-1 text-sm hover:bg-papel"
            >
              {tr('Revisar', 'Review')}
            </button>
            <button type="button" onClick={() => setAbierto(false)} className="text-xs text-apagado hover:text-tinta">
              {tr('Cancelar', 'Cancel')}
            </button>
          </form>
          {mensaje && (
            <p role="status" className={`mt-2 text-xs ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
              {mensaje.texto}
            </p>
          )}
          {revision && (
            <div className="mt-2 space-y-1" data-prueba="revision-lote-bb">
              <p>
                {tr(
                  `Cambian ${numero(revision.cambian)} · ya lo tenían ${numero(revision.sinCambios)} · no se pueden cambiar ${numero(revision.errores)}.`,
                  `${numero(revision.cambian)} change · ${numero(revision.sinCambios)} already had it · ${numero(revision.errores)} cannot be changed.`,
                )}
              </p>
              <ul className="max-h-40 overflow-auto text-xs text-apagado">
                {revision.bolsas
                  .filter(b => b.resultado !== 'sin_cambios')
                  .slice(0, 100)
                  .map(b => (
                    <li key={b.bbNo} className={b.resultado === 'error' ? 'text-alerta' : ''}>
                      {b.bbNo}:{' '}
                      {b.resultado === 'error'
                        ? `⚠ ${b.mensaje ?? ''}`
                        : `${texto(b.antes[campo])} → ${texto(b.despues[campo])}`}
                    </li>
                  ))}
              </ul>
              {revision.errores > 0 && (
                <p className="text-xs text-alerta">
                  {tr(
                    'Quita de la selección los que no se pueden cambiar para poder aplicar.',
                    'Untick the ones that cannot be changed to be able to apply.',
                  )}
                </p>
              )}
              {revision.cambian > 0 && revision.errores === 0 && (
                <button
                  type="button"
                  onClick={() => void ejecutar('aplicar')}
                  disabled={cambiar.isPending}
                  className="rounded-md bg-verde px-3 py-1 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
                >
                  {cambiar.isPending
                    ? tr('Aplicando…', 'Applying…')
                    : tr(
                        `Aplicar a ${numero(revision.cambian)} Big Bags`,
                        `Apply to ${numero(revision.cambian)} Big Bags`,
                      )}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
